import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-time-doctor";

const FAQ_ITEMS = [
  {
    question: "What happens if I don't renew my subscription?",
    answer: "If your subscription isn't renewed before the due date, your TeamGrid account will be temporarily locked, and access will be paused. However, your data remains safe and will be fully restored once the subscription is reactivated.",
  },
  {
    question: "Can we store data on our own cloud with TeamGrid?",
    answer: "By default, TeamGrid stores all data securely on its managed cloud servers. If you require data to be stored on your own cloud infrastructure, custom deployment options may be available for enterprise plans. Please contact our team to discuss specific requirements.",
  },
  {
    question: "Is TeamGrid compatible with a work-hierarchy structure?",
    answer: "Yes. TeamGrid is designed with a role-based hierarchy that supports organization-level, team-level, and individual user roles. This allows for structured access control, making it ideal for companies with multi-level team setups and layered management structures.",
  },
  {
    question: "What is the Organizational Intelligence Engine?",
    answer: "It's TeamGrid's core AI feature that detects team inefficiencies, identifies improvement areas, and surfaces performance issues in real time automatically.",
  },
  {
    question: "Is TeamGrid suitable for remote and hybrid teams?",
    answer: "Yes. TeamGrid is built for remote and hybrid team optimization. We currently offer a desktop application for data tracking and a secure web portal accessible via browser. To view and analyze tracking data, you can use the web portal dashboard, which provides real-time visibility into team performance. Please note that TeamGrid does not provide a web application; all functionality is accessed through the desktop app and browser-based portal.",
  },
  {
    question: "What makes Ask TeamGrid different from Time Doctor's reporting?",
    answer: "Ask TeamGrid is an AI chat assistant that answers questions conversationally and delivers instant, actionable insights. Time Doctor requires manual CSV interpretation with no AI assistance.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "What happens if I don't renew my subscription?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "If your subscription isn't renewed before the due date, your TeamGrid account will be temporarily locked. All data remains safe and is fully restored once the subscription is reactivated.",
      },
    },
    {
      "@type": "Question",
      name: "Can we store data on our own cloud with TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid stores data on secure managed cloud servers by default. Enterprise customers may request custom deployment options for their own cloud infrastructure.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid compatible with a work-hierarchy structure?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid supports role-based access across organization, team, and individual levels, making it ideal for multi-layered team structures.",
      },
    },
    {
      "@type": "Question",
      name: "What is the Organizational Intelligence Engine?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It is TeamGrid's AI system that automatically detects inefficiencies, improvement areas, and performance risks in real time.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid suitable for remote and hybrid teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid is designed for remote and hybrid teams with a desktop tracking app and browser-based analytics portal.",
      },
    },
    {
      "@type": "Question",
      name: "What makes Ask TeamGrid different from Time Doctor reporting?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ask TeamGrid is an AI chat assistant that delivers instant, actionable insights conversationally, while Time Doctor relies on manual dashboards and CSV exports.",
      },
    },
  ],
};

const COMPARISON_TABLE_ROWS = [
  { feature: "AI-Driven Insights", teamgrid: "Designed around AI-powered summaries and recommendations", competitor: "Primarily focused on activity-based reporting" },
  { feature: "AI Chat Experience", teamgrid: "Includes an interactive AI assistant for insights", competitor: "Reporting is largely dashboard and export-based" },
  { feature: "Privacy Approach", teamgrid: "Designed to emphasize trust and transparency", competitor: "Emphasizes detailed activity tracking" },
  { feature: "Analytics Style", teamgrid: "Real-time summaries and organizational insights", competitor: "Time and activity logs" },
  { feature: "Reporting", teamgrid: "Automated summaries with export options", competitor: "Manual report configuration" },
  { feature: "UI Experience", teamgrid: "Modern, streamlined interface", competitor: "Functional, data-heavy interface" },
];

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "Designed to deliver performance insights without invasive tracking",
  "Ask TeamGrid provides instant, AI-powered visibility into team performance",
  "Encourages trust and transparency across the organization",
  "Modern, intuitive interface built for smooth day-to-day use",
  "Easy onboarding with a desktop app for tracking and a web portal for performance analysis",
];

const DATA_DECISIONS_COMPETITOR_BULLETS = [
  "Includes monitoring features that may feel intrusive to employees",
  "Adoption can be difficult in remote-first or people-centric teams",
];

const DATA_DECISIONS2_TEAMGRID_BULLETS = [
  "A privacy-first alternative to Time Doctor designed for clarity, not data overload",
  "Organizational Intelligence Engine automatically highlights friction points and inefficiencies",
  "Daily, hourly, and weekly summaries present performance trends at a glance",
  "Reports are instantly generated, downloadable, and easy to share across teams",
  "Helps managers focus on decision-making instead of data crunching",
  "Built to support continuous improvement, not just track activity",
];

const DATA_DECISIONS2_COMPETITOR_BULLETS = [
  "Provides activity data primarily in raw formats that require manual interpretation",
  "Managers often spend time exporting CSVs and analyzing reports outside the platform",
  "Limited built-in intelligence for proactively surfacing performance trends or issues",
  "Focuses more on recording activity than guiding next steps",
];

const WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid - An AI-powered chat assistant that delivers on-demand performance insights instantly",
  "Organizational Intelligence Engine - Identifies friction points, inefficiencies, and performance trends automatically",
  "Real-Time Dashboards - Visualize team progress, task alignment, and goal tracking as it happens",
  "Smart Summaries - Daily, hourly, and weekly views to monitor productivity patterns without manual tracking",
  "Privacy-First Design - Built without surveillance features; respects employee trust and autonomy",
  "One-Click Report Downloads - Easily share customizable insights with stakeholders",
  "Remote & Hybrid Team Support - Optimize distributed teams with real-time visibility across platforms",
  "Fast, Intuitive Onboarding - Get teams up and running quickly with a modern, easy-to-use interface",
];

const TimeDoctorComparison = () => (
  <ComparisonPageTemplate
    seoTitle="Looking for a Time Doctor Alternative? | TeamGrid"
    seoDescription="Compare TeamGrid vs Time Doctor. Choose insights over monitoring with AI-powered workforce intelligence, privacy-first design, and real-time analytics."
    seoCanonical={CANONICAL_URL}
    jsonLdFaq={JSON_LD_FAQ}
    heroBadge="Comparison Guide"
    heroTitleFontSize="clamp(22px, 2.8vw, 48px)"
    heroTitleLine1="Looking for a Time Doctor Alternative?"
    heroTitleLine2="Choose Insights Over Monitoring"
    heroTitleLine2Teal
    heroTitleLine3="with TeamGrid"
    heroDescriptionLines={[
      "TeamGrid is a modern, AI-powered Time Doctor alternative that delivers",
      "real-time performance insights without surveillance built for teams that value",
      "trust, transparency, and smart decision-making.",
    ]}
    heroDescriptionLinesNoWrap
    heroDescriptionFontSize={20}
    heroDescriptionLineHeight="29px"
    competitorName="Time Doctor"
    competitorLogoUrl="/td_logo.webp"
    comparisonTableRows={COMPARISON_TABLE_ROWS}
    dataDecisionsHeading="Choose Trust & Clarity Over Control"
    dataDecisionsSubtitle="Empower teams with insight, not oversight."
    dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
    dataDecisionsCompetitorBullets={DATA_DECISIONS_COMPETITOR_BULLETS}
    dataDecisionsCompetitorCardTitle="Time Doctor"
    dataDecisions2Heading="Make the Shift from Raw Data to Real-Time Insight"
    dataDecisions2Subtitle="Raw activity data only matters when it turns into clear, timely decisions. That's why many teams exploring Time Doctor alternatives are moving toward real-time, AI-driven workforce intelligence software that turns activity data into clear, actionable decisions."
    dataDecisions2TeamGridBullets={DATA_DECISIONS2_TEAMGRID_BULLETS}
    dataDecisions2CompetitorBullets={DATA_DECISIONS2_COMPETITOR_BULLETS}
    dataDecisions2CompetitorCardTitle="Time Doctor"
    dataDecisions2ConstrainHeight
    keyFeaturesSectionWhyTeamsSwitching
    whatYouGetItems={WHAT_YOU_GET_ITEMS}
    whatYouGetSectionPadding80_384
    outcomesImageSrc="/td_section_bottom.webp"
    outcomesImageAlt="Performance Outcomes - Faster decision making with AI-powered summaries, Higher team adoption with a privacy first approach, Reduced manual reporting and analysis time, Optimized for modern remote and hybrid teams"
    trustedBadges={[
      "Privacy-first time tracking",
      "Ask TeamGrid AI assistant",
      "Real-time analytics & insights",
      "Free for up to 5 users",
    ]}
    faqs={FAQ_ITEMS}
    faqTitle="Frequently Asked Questions"
    faqSubtitle="Common questions about TeamGrid vs. Time Doctor."
  />
);

export default TimeDoctorComparison;
